import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useProjectCsvMappingQuery,
  useUpdateProjectCsvMapping,
} from '@/features/projects/hooks/useProjectCsvMapping';
import { useMutationErrorSnackbar } from '@/shared/hooks';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Box, Panel, Stack, Text } from '@/ui';
import {
  settingsCopyTextSx,
  settingsPanelSurfaceSx,
} from '@/ui/composites/settingsPageTypographySx';
import {
  CheckCircle,
  CloseIcon,
  ErrorOutline,
  ExpandIcon,
  HelpOutline,
  InfoOutlined,
  Tooltip,
} from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { IconButton } from '@/ui/primitives/IconButton';
import { SideLabelTextField } from '@/ui/primitives/SideLabelTextField';
import {
  defaultCsvHeaderAliases,
  defaultCsvStatusAliases,
  type CsvHeaderAliases,
  type CsvStatusAliases,
} from '@flowqa/shared';
import { parseValidationRows } from './csvValidationFileParser';
import { DetectedMappingValuesSection } from './DetectedMappingValuesSection';
import {
  buildHeaderMapping,
  buildStatusMapping,
  fieldOptions,
  mappingTooltipSlotProps,
  type ValidationStatus,
} from './projectCsvMappingSectionShared';

type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };
type ProjectCsvMappingSectionProps = {
  projectId: string | null;
  notify: (input: NotifyInput) => void;
};

export const ProjectCsvMappingSection = ({
  projectId,
  notify,
}: ProjectCsvMappingSectionProps): ReactElement => {
  const location = useLocation();
  const accordionRef = useRef<HTMLDivElement | null>(null);
  const isHashTargeted = location.hash === '#xlsx-csv-mapping';
  const defaultHeaderMapping = useMemo(() => buildHeaderMapping(defaultCsvHeaderAliases), []);
  const defaultStatusMapping = useMemo(() => buildStatusMapping(defaultCsvStatusAliases), []);
  const [mapping, setMapping] = useState<Record<string, string>>(defaultHeaderMapping);
  const [draftMapping, setDraftMapping] = useState<Record<string, string>>(defaultHeaderMapping);
  const [statusAliases, setStatusAliases] = useState<Record<string, string>>(defaultStatusMapping);
  const [draftStatusAliases, setDraftStatusAliases] =
    useState<Record<string, string>>(defaultStatusMapping);
  const [expanded, setExpanded] = useState(() => isHashTargeted);
  const [headerValidation, setHeaderValidation] = useState<Record<string, ValidationStatus>>(() =>
    fieldOptions.reduce<Record<string, ValidationStatus>>((acc, field) => {
      acc[field.key] = 'unknown';
      return acc;
    }, {}),
  );
  const [statusValidation, setStatusValidation] = useState<Record<string, ValidationStatus>>(
    () => ({
      passed: 'unknown',
      failed: 'unknown',
      errored: 'unknown',
      skipped: 'unknown',
      untested: 'unknown',
    }),
  );
  const [validatedCsvHeaders, setValidatedCsvHeaders] = useState<string[]>([]);
  const [validatedCsvStatuses, setValidatedCsvStatuses] = useState<string[]>([]);
  const isDirty =
    fieldOptions.some((field) => mapping[field.key] !== draftMapping[field.key]) ||
    Object.keys(statusAliases).some((key) => statusAliases[key] !== draftStatusAliases[key]);
  const csvMappingQuery = useProjectCsvMappingQuery(projectId);
  const updateCsvMapping = useUpdateProjectCsvMapping();
  const csvMappingError = useMutationErrorSnackbar();
  const updateDisabled =
    !projectId || !isDirty || updateCsvMapping.isPending || csvMappingQuery.isPending;
  const clearDisabled =
    !Object.values(headerValidation).some((status) => status !== 'unknown') &&
    !Object.values(statusValidation).some((status) => status !== 'unknown');
  const validateInputRef = useRef<HTMLInputElement | null>(null);

  const resetValidationStates = useCallback(() => {
    setHeaderValidation((current) => {
      const next = { ...current };
      Object.keys(next).forEach((key) => {
        next[key] = 'unknown';
      });
      return next;
    });
    setStatusValidation((current) => {
      const next = { ...current };
      Object.keys(next).forEach((key) => {
        next[key] = 'unknown';
      });
      return next;
    });
  }, []);

  const clearValidation = useCallback(() => {
    resetValidationStates();
    setValidatedCsvHeaders([]);
    setValidatedCsvStatuses([]);
  }, [resetValidationStates]);

  useEffect(() => {
    if (!isHashTargeted) return;
    setExpanded(true);
  }, [isHashTargeted]);

  useEffect(() => {
    if (!isHashTargeted) return;
    if (!expanded) return;
    const timer = window.setTimeout(() => {
      accordionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 150);
    return () => {
      window.clearTimeout(timer);
    };
  }, [expanded, isHashTargeted]);

  useEffect(() => {
    if (!projectId) return;
    setMapping(defaultHeaderMapping);
    setDraftMapping(defaultHeaderMapping);
    setStatusAliases(defaultStatusMapping);
    setDraftStatusAliases(defaultStatusMapping);
    clearValidation();
  }, [clearValidation, defaultHeaderMapping, defaultStatusMapping, projectId]);

  useEffect(() => {
    if (!csvMappingQuery.data) return;
    const nextHeaderMapping = buildHeaderMapping(csvMappingQuery.data.headerAliases);
    const nextStatusMapping = buildStatusMapping(csvMappingQuery.data.statusAliases);
    setMapping(nextHeaderMapping);
    setDraftMapping(nextHeaderMapping);
    setStatusAliases(nextStatusMapping);
    setDraftStatusAliases(nextStatusMapping);
    clearValidation();
  }, [clearValidation, csvMappingQuery.data]);

  const buildInfoAdornment = useCallback(
    (message: string): ReactElement => (
      <Tooltip title={message} placement="top" arrow slotProps={mappingTooltipSlotProps}>
        <InfoOutlined
          fontSize="inherit"
          sx={(theme) => ({
            color: resolveSemanticColor(theme, 'textSecondary'),
            fontSize: 20,
          })}
        />
      </Tooltip>
    ),
    [],
  );

  const normalizeHeader = useCallback((value: string): string => value.trim().toLowerCase(), []);
  const normalizeStatus = useCallback(
    (value: string): string =>
      value
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, ''),
    [],
  );
  const parseAliasList = useCallback(
    (value: string): string[] =>
      value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0),
    [],
  );
  const syncValidationWithDetected = useCallback(
    (nextDraftMapping: Record<string, string>, nextDraftStatusAliases: Record<string, string>) => {
      if (validatedCsvHeaders.length === 0) {
        resetValidationStates();
        return;
      }

      const headerSet = new Set(
        validatedCsvHeaders
          .map((header) => normalizeHeader(header))
          .filter((header) => header.length > 0),
      );
      const nextHeaderValidation: Record<string, ValidationStatus> = {};
      fieldOptions.forEach((field) => {
        const aliases = parseAliasList(nextDraftMapping[field.key]).map(normalizeHeader);
        if (aliases.length === 0) {
          nextHeaderValidation[field.key] = 'unknown';
          return;
        }
        nextHeaderValidation[field.key] = aliases.some((alias) => headerSet.has(alias))
          ? 'valid'
          : 'invalid';
      });
      setHeaderValidation(nextHeaderValidation);

      const statusSet = new Set(
        validatedCsvStatuses
          .map((statusValue) => normalizeStatus(statusValue))
          .filter((statusValue) => statusValue.length > 0),
      );
      const nextStatusValidation: Record<string, ValidationStatus> = { ...statusValidation };
      Object.keys(defaultCsvStatusAliases).forEach((key) => {
        const aliases = parseAliasList(nextDraftStatusAliases[key]).map(normalizeStatus);
        if (aliases.length === 0) {
          nextStatusValidation[key] = 'unknown';
          return;
        }
        nextStatusValidation[key] = aliases.some((alias) => statusSet.has(alias))
          ? 'valid'
          : 'invalid';
      });
      setStatusValidation(nextStatusValidation);
    },
    [
      fieldOptions,
      normalizeHeader,
      normalizeStatus,
      parseAliasList,
      resetValidationStates,
      statusValidation,
      validatedCsvHeaders,
      validatedCsvStatuses,
    ],
  );

  const resolveValidationIcon = useCallback((status: ValidationStatus): ReactElement => {
    if (status === 'valid') {
      return (
        <Tooltip title="Matched" placement="top" arrow slotProps={mappingTooltipSlotProps}>
          <CheckCircle
            fontSize="small"
            sx={(theme) => ({ color: resolveSemanticColor(theme, 'success') })}
          />
        </Tooltip>
      );
    }
    if (status === 'invalid') {
      return (
        <Tooltip title="No match found" placement="top" arrow slotProps={mappingTooltipSlotProps}>
          <ErrorOutline
            fontSize="small"
            sx={(theme) => ({ color: resolveSemanticColor(theme, 'error') })}
          />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Not yet validated" placement="top" arrow slotProps={mappingTooltipSlotProps}>
        <HelpOutline
          fontSize="small"
          sx={(theme) => ({ color: resolveSemanticColor(theme, 'textSecondary') })}
        />
      </Tooltip>
    );
  }, []);

  const handleValidateFile = useCallback(
    async (file: File): Promise<void> => {
      const rows = await parseValidationRows(file);
      if (rows.length === 0) {
        setValidatedCsvHeaders([]);
        setValidatedCsvStatuses([]);
        setHeaderValidation((current) => {
          const next = { ...current };
          fieldOptions.forEach((field) => {
            const aliases = parseAliasList(draftMapping[field.key]);
            next[field.key] = aliases.length > 0 ? 'invalid' : 'unknown';
          });
          return next;
        });
        setStatusValidation((current) => {
          const next = { ...current };
          Object.keys(defaultCsvStatusAliases).forEach((key) => {
            const aliases = parseAliasList(draftStatusAliases[key]);
            next[key] = aliases.length > 0 ? 'invalid' : 'unknown';
          });
          return next;
        });
        return;
      }

      const headerRow = rows[0] ?? [];
      setValidatedCsvHeaders(
        headerRow.map((header) => header.trim()).filter((header) => header.length > 0),
      );
      const headers = headerRow.map((header) => normalizeHeader(header));
      const headerSet = new Set(headers.filter((header) => header.length > 0));

      const nextHeaderValidation: Record<string, ValidationStatus> = {};
      fieldOptions.forEach((field) => {
        const aliases = parseAliasList(draftMapping[field.key]).map(normalizeHeader);
        if (aliases.length === 0) {
          nextHeaderValidation[field.key] = 'unknown';
          return;
        }
        const matches = aliases.some((alias) => headerSet.has(alias));
        nextHeaderValidation[field.key] = matches ? 'valid' : 'invalid';
      });
      setHeaderValidation(nextHeaderValidation);

      const statusHeaderAliases = parseAliasList(draftMapping.status).map(normalizeHeader);
      const statusHeaderIndex = headers.findIndex((header) => statusHeaderAliases.includes(header));
      if (statusHeaderIndex === -1) {
        setValidatedCsvStatuses([]);
        setStatusValidation((current) => {
          const next = { ...current };
          Object.keys(defaultCsvStatusAliases).forEach((key) => {
            const aliases = parseAliasList(draftStatusAliases[key]);
            next[key] = aliases.length > 0 ? 'invalid' : 'unknown';
          });
          return next;
        });
        return;
      }

      const statusValues = new Set<string>();
      const statusValueByNormalized = new Map<string, string>();
      rows.slice(1, 201).forEach((row) => {
        if (statusHeaderIndex >= row.length) return;
        const rawValue = row[statusHeaderIndex];
        const trimmedValue = rawValue.trim();
        const normalizedValue = normalizeStatus(rawValue);
        if (normalizedValue.length > 0) {
          statusValues.add(normalizedValue);
          if (!statusValueByNormalized.has(normalizedValue) && trimmedValue.length > 0) {
            statusValueByNormalized.set(normalizedValue, trimmedValue);
          }
        }
      });
      setValidatedCsvStatuses(Array.from(statusValueByNormalized.values()));

      const nextStatusValidation: Record<string, ValidationStatus> = { ...statusValidation };
      Object.keys(defaultCsvStatusAliases).forEach((key) => {
        const aliases = parseAliasList(draftStatusAliases[key]).map(normalizeStatus);
        if (aliases.length === 0) {
          nextStatusValidation[key] = 'unknown';
          return;
        }
        const matches = aliases.some((alias) => statusValues.has(alias));
        nextStatusValidation[key] = matches ? 'valid' : 'invalid';
      });
      setStatusValidation(nextStatusValidation);
    },
    [
      draftMapping,
      draftStatusAliases,
      fieldOptions,
      normalizeHeader,
      normalizeStatus,
      parseValidationRows,
      parseAliasList,
      statusValidation,
    ],
  );
  const matchedUploadedStatuses = useMemo<Set<string>>(() => {
    const aliasSet = new Set<string>();
    Object.keys(draftStatusAliases).forEach((statusKey) => {
      parseAliasList(draftStatusAliases[statusKey]).forEach((alias) => {
        const normalizedAlias = normalizeStatus(alias);
        if (normalizedAlias.length > 0) aliasSet.add(normalizedAlias);
      });
    });
    return new Set(
      validatedCsvStatuses
        .map((statusValue) => normalizeStatus(statusValue))
        .filter((statusValue) => aliasSet.has(statusValue)),
    );
  }, [draftStatusAliases, normalizeStatus, parseAliasList, validatedCsvStatuses]);
  const matchedUploadedHeaders = useMemo<Set<string>>(() => {
    const aliasSet = new Set<string>();
    fieldOptions.forEach((field) => {
      parseAliasList(draftMapping[field.key]).forEach((alias) => {
        const normalizedAlias = normalizeHeader(alias);
        if (normalizedAlias.length > 0) aliasSet.add(normalizedAlias);
      });
    });
    return new Set(
      validatedCsvHeaders
        .map((header) => normalizeHeader(header))
        .filter((header) => aliasSet.has(header)),
    );
  }, [draftMapping, fieldOptions, normalizeHeader, parseAliasList, validatedCsvHeaders]);

  return (
    <Accordion
      ref={accordionRef}
      disableGutters
      elevation={0}
      square
      expanded={expanded}
      slotProps={isHashTargeted ? { transition: { timeout: 0 } } : undefined}
      onChange={(event, nextExpanded) => {
        const target = event.target as HTMLElement | null;
        if (!target?.closest('.MuiAccordionSummary-expandIconWrapper')) {
          return;
        }
        setExpanded(nextExpanded);
      }}
      sx={[
        settingsPanelSurfaceSx,
        {
          border: (theme) => `1px solid ${resolveSemanticColor(theme, 'border')}`,
          borderRadius: s('sm'),
          overflow: 'hidden',
          padding: s('lg'),
          '&::before': { display: 'none' },
        },
      ]}
    >
      <AccordionSummary
        expandIcon={<ExpandIcon fontSize="medium" sx={{ fontSize: 28 }} />}
        sx={{
          padding: s('none'),
          minHeight: 'auto',
          alignItems: 'flex-start',
          cursor: 'default !important',
          userSelect: 'text',
          WebkitUserSelect: 'text',
          '&.MuiButtonBase-root': {
            cursor: 'default !important',
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-content': {
            margin: 0,
            alignItems: 'flex-start',
            cursor: 'default !important',
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-content .MuiTypography-root': {
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            alignSelf: 'center',
            cursor: 'pointer',
            pointerEvents: 'auto',
          },
        }}
      >
        <Stack gap="xs" sx={{ width: '100%' }}>
          <Text role="sectionTitle">XLSX / CSV Mapping</Text>
          <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
            Update header and status aliases for XLSX or CSV results file uploads.
          </Text>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: s('none'), paddingTop: s('lg') }}>
        <Panel container="none" padding="xs">
          <Stack gap="md">
            <Stack gap="md" sx={{ paddingBottom: s('md') }}>
              <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                Update your aliases below in a comma delimited list. Non-matched headers will be
                ignored.
              </Text>
              <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                XLSX results should be contained in the first sheet, with headers in the first row.
              </Text>
            </Stack>
            <input
              ref={validateInputRef}
              type="file"
              hidden
              accept=".csv,.xlsx"
              onChange={(event) => {
                const files = event.target.files;
                const file = files && files.length > 0 ? files[0] : null;
                if (file) void handleValidateFile(file);
                event.target.value = '';
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: s('md'),
                width: '100%',
                flexWrap: 'wrap',
              }}
            >
              <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
                Validate a XLSX or CSV file before saving changes.
              </Text>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: s('sm') }}>
                <Button
                  variant="secondary"
                  fullWidthOnMobile={false}
                  onClick={() => {
                    validateInputRef.current?.click();
                  }}
                >
                  Validate File
                </Button>
                <Tooltip title="Clear" placement="top" arrow slotProps={mappingTooltipSlotProps}>
                  <IconButton
                    aria-label="Clear validation"
                    size="small"
                    disabled={clearDisabled}
                    onClick={() => {
                      clearValidation();
                    }}
                    sx={(theme) => ({
                      color: resolveSemanticColor(theme, 'textSecondary'),
                      ml: s('xs'),
                      mr: `-${s('sm')}`,
                      borderRadius: '999px',
                      padding: s('xs'),
                    })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <DetectedMappingValuesSection
              title="Detected Headers"
              values={validatedCsvHeaders}
              isMatched={(value) => matchedUploadedHeaders.has(normalizeHeader(value))}
            />
            <Text
              role="bodyStrong"
              color="textSecondary"
              sx={[settingsCopyTextSx, { pt: s('sm'), pb: s('xs') }]}
            >
              Header Mapping
            </Text>
            <Stack gap="lg">
              {fieldOptions.map((field) => (
                <SideLabelTextField
                  key={field.key}
                  fieldStyle="settings"
                  label={field.label}
                  value={draftMapping[field.key]}
                  placeholder="Enter header name"
                  labelAdornment={
                    field.key === 'suite'
                      ? buildInfoAdornment('Optional. Falls back to the uploaded file name.')
                      : field.key === 'status'
                        ? buildInfoAdornment('Required to track executions.')
                        : field.key === 'duration'
                          ? buildInfoAdornment('Optional.')
                          : field.key === 'timestamp'
                            ? buildInfoAdornment('Optional. Inferred by upload date if omitted.')
                            : undefined
                  }
                  endAdornment={resolveValidationIcon(headerValidation[field.key] ?? 'unknown')}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    const nextDraftMapping = { ...draftMapping, [field.key]: nextValue };
                    setDraftMapping(nextDraftMapping);
                    syncValidationWithDetected(nextDraftMapping, draftStatusAliases);
                  }}
                />
              ))}
            </Stack>
            <Stack gap="sm">
              <DetectedMappingValuesSection
                title="Detected Statuses"
                values={validatedCsvStatuses}
                isMatched={(value) => matchedUploadedStatuses.has(normalizeStatus(value))}
              />
              <Text
                role="bodyStrong"
                color="textSecondary"
                sx={[settingsCopyTextSx, { pt: s('sm'), pb: s('xs') }]}
              >
                Status Mapping
              </Text>
              <Stack gap="lg">
                <SideLabelTextField
                  fieldStyle="settings"
                  label="Passed"
                  value={draftStatusAliases.passed}
                  placeholder="Enter status aliases"
                  endAdornment={resolveValidationIcon(statusValidation.passed)}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    const nextDraftStatusAliases = { ...draftStatusAliases, passed: nextValue };
                    setDraftStatusAliases(nextDraftStatusAliases);
                    syncValidationWithDetected(draftMapping, nextDraftStatusAliases);
                  }}
                />
                <SideLabelTextField
                  fieldStyle="settings"
                  label="Failed"
                  value={draftStatusAliases.failed}
                  placeholder="Enter status aliases"
                  endAdornment={resolveValidationIcon(statusValidation.failed)}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    const nextDraftStatusAliases = { ...draftStatusAliases, failed: nextValue };
                    setDraftStatusAliases(nextDraftStatusAliases);
                    syncValidationWithDetected(draftMapping, nextDraftStatusAliases);
                  }}
                />
                <SideLabelTextField
                  fieldStyle="settings"
                  label="Errored"
                  value={draftStatusAliases.errored}
                  placeholder="Enter status aliases"
                  endAdornment={resolveValidationIcon(statusValidation.errored)}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    const nextDraftStatusAliases = { ...draftStatusAliases, errored: nextValue };
                    setDraftStatusAliases(nextDraftStatusAliases);
                    syncValidationWithDetected(draftMapping, nextDraftStatusAliases);
                  }}
                />
                <SideLabelTextField
                  fieldStyle="settings"
                  label="Skipped"
                  value={draftStatusAliases.skipped}
                  placeholder="Enter status aliases"
                  endAdornment={resolveValidationIcon(statusValidation.skipped)}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    const nextDraftStatusAliases = { ...draftStatusAliases, skipped: nextValue };
                    setDraftStatusAliases(nextDraftStatusAliases);
                    syncValidationWithDetected(draftMapping, nextDraftStatusAliases);
                  }}
                />
                <SideLabelTextField
                  fieldStyle="settings"
                  label="Untested"
                  value={draftStatusAliases.untested}
                  placeholder="Enter status aliases"
                  labelAdornment={buildInfoAdornment('Empty values are also treated as untested.')}
                  endAdornment={resolveValidationIcon(statusValidation.untested)}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    const nextDraftStatusAliases = { ...draftStatusAliases, untested: nextValue };
                    setDraftStatusAliases(nextDraftStatusAliases);
                    syncValidationWithDetected(draftMapping, nextDraftStatusAliases);
                  }}
                />
              </Stack>
            </Stack>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: s('md'),
                width: '100%',
                marginTop: s('md'),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: s('sm') }}>
                <Button
                  variant="ghost"
                  fullWidthOnMobile={false}
                  disabled={updateDisabled}
                  onClick={() => {
                    const nextDraftMapping = { ...mapping };
                    const nextDraftStatusAliases = { ...statusAliases };
                    setDraftMapping(nextDraftMapping);
                    setDraftStatusAliases(nextDraftStatusAliases);
                    syncValidationWithDetected(nextDraftMapping, nextDraftStatusAliases);
                    csvMappingError.clearError();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  fullWidthOnMobile={false}
                  disabled={updateDisabled}
                  onClick={() => {
                    if (!projectId) return;
                    const suiteAliases = parseAliasList(draftMapping.suite);
                    const testAliases = parseAliasList(draftMapping.test);
                    const statusAliasesList = parseAliasList(draftMapping.status);
                    const durationAliases = parseAliasList(draftMapping.duration);
                    const timestampAliases = parseAliasList(draftMapping.timestamp);
                    const headerAliases: CsvHeaderAliases = {
                      suite: suiteAliases,
                      test: testAliases,
                      status: statusAliasesList,
                      ...(durationAliases.length > 0 ? { duration: durationAliases } : {}),
                      ...(timestampAliases.length > 0 ? { timestamp: timestampAliases } : {}),
                    };
                    const nextStatusAliases: CsvStatusAliases = {
                      passed: parseAliasList(draftStatusAliases.passed),
                      failed: parseAliasList(draftStatusAliases.failed),
                      errored: parseAliasList(draftStatusAliases.errored),
                      skipped: parseAliasList(draftStatusAliases.skipped),
                      untested: parseAliasList(draftStatusAliases.untested),
                    };
                    updateCsvMapping.mutate(
                      { projectId, headerAliases, statusAliases: nextStatusAliases },
                      {
                        onSuccess: (updated) => {
                          const nextHeaderMapping = buildHeaderMapping(updated.headerAliases);
                          const nextStatusMapping = buildStatusMapping(updated.statusAliases);
                          setMapping(nextHeaderMapping);
                          setDraftMapping(nextHeaderMapping);
                          setStatusAliases(nextStatusMapping);
                          setDraftStatusAliases(nextStatusMapping);
                          syncValidationWithDetected(nextHeaderMapping, nextStatusMapping);
                          csvMappingError.clearError();
                          notify({ message: 'Updated CSV mapping', tone: 'green' });
                        },
                        onError: (err) => {
                          csvMappingError.setError(err);
                        },
                      },
                    );
                  }}
                >
                  Update
                </Button>
              </Box>
            </Box>
          </Stack>
        </Panel>
      </AccordionDetails>
    </Accordion>
  );
};
